/**
 * Utility functions for reading and managing cookies
 *
 * Note: These utilities are browser-only and require `document.cookie`.
 * Do not use in server-side rendering (SSR) or Node.js contexts.
 */

/**
 * Get a specific cookie value by name
 * @param {string} name - The name of the cookie
 * @returns {string|null} - The cookie value or null if not found
 */
export const getCookie = name => {
  try {
    const cookies = document.cookie.split(';');
    const cookie = cookies.find(cookie => cookie.trim().startsWith(`${name}=`));

    if (cookie) {
      const value = cookie.substring(cookie.indexOf('=') + 1);
      return decodeURIComponent(value);
    }
  } catch (error) {
    console.warn(`Failed to read cookie ${name}:`, error);
  }
  return null;
};

/**
 * Get user data from the userData cookie
 * @param {string} name - The name of the cookie
 * @returns {Object|null} - User data object or null if not found
 */
export const getDataFromCookie = name => {
  try {
    const userDataString = getCookie(name);
    if (userDataString) {
      return JSON.parse(userDataString);
    }
  } catch (error) {
    console.warn(`Failed to parse cookie ${name}:`, error);
  }
  return null;
};

/**
 * Set a cookie with specified options
 * @param {string} name - Cookie name
 * @param {string|Object} value - Cookie value (string or object that will be JSON stringified)
 * @param {Object} options - Cookie options (domain, path, expires, etc.)
 */
export const setCookie = (name, value, options = {}) => {
  try {
    const {
      domain,
      path = '/',
      expires,
      secure = true,
      sameSite = 'Strict',
    } = options;

    // Handle both string and object values
    const stringValue = typeof value === 'object' ? JSON.stringify(value) : value;
    let cookieString = `${name}=${stringValue}`;

    // Check if running on localhost (Safari requires special handling)
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '' ||
        window.location.protocol === 'file:');

    // Don't set domain on localhost (Safari doesn't support it)
    if (domain && !isLocalhost) cookieString += `; domain=${domain}`;
    if (path) cookieString += `; path=${path}`;
    if (expires) cookieString += `; expires=${expires}`;

    // Only set Secure flag if not localhost and secure is true (Safari requires HTTPS for Secure)
    if (secure && !isLocalhost) cookieString += `; Secure`;

    // Use Lax instead of Strict for localhost (better Safari compatibility)
    if (sameSite) {
      const sameSiteValue = isLocalhost && sameSite === 'Strict' ? 'Lax' : sameSite;
      cookieString += `; SameSite=${sameSiteValue}`;
    }

    document.cookie = cookieString;
  } catch (error) {
    console.warn(`Failed to set cookie ${name}:`, error);
  }
};

/**
 * Delete a cookie by setting it to expire in the past
 * @param {string} name - Cookie name
 * @param {string} domain - Cookie domain (optional)
 * @param {string} path - Cookie path (optional, defaults to '/')
 */
export const deleteCookie = (name, domain, path = '/') => {
  try {
    // Check if running on localhost (Safari requires special handling)
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '' ||
        window.location.protocol === 'file:');

    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

    // Don't set domain on localhost (Safari doesn't support it)
    if (domain && !isLocalhost) cookieString += `; domain=${domain}`;
    if (path) cookieString += `; path=${path}`;

    document.cookie = cookieString;
  } catch (error) {
    console.warn(`Failed to delete cookie ${name}:`, error);
  }
};
